import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FilterPill } from '../components/FilterPill'
import { Icon } from '../components/Icon'
import { ResourceEditModal } from '../components/ResourceEditModal'
import { useResourcesDirectory } from '../hooks/useResourcesDirectory'
import { useResourceOverrides } from '../hooks/useResourceOverrides'
import {
  EVENT_RESOURCE_CATEGORIES,
  EVENT_RESOURCE_CATEGORY_LABEL
} from '../types'
import type {
  EventResourceCategory,
  ResourcesDirectoryEvent
} from '../types'

// ResourcesDirectory — cross-event matrix surfacing every (event ×
// resource category) intersection so editors can spot gaps before
// event day (spec 46).
//
// Rows: non-archived events, newest start_date first.
// Columns: the 6 EVENT_RESOURCE_CATEGORIES + a Coverage indicator.
// Cells: count badge (brass-soft for populated, parchment / dashed
//        border for empty). Clicking any cell — populated or empty —
//        navigates to /events/{id}?openResources={category} and the
//        ResourcesPanel opens to that tab.
//
// Filter pills act at the event-row level (not per-cell):
//   All       — every non-archived event.
//   Populated — events with at least one row in every category.
//   Missing   — events with at least one empty category.
//
// Archived events are excluded server-side; no toggle in v1 — keeps
// the surface honest about "things to act on now".

type Filter = 'all' | 'populated' | 'missing'

// Editor modal state — null when closed. When set, opens the
// ResourceEditModal in either defaults mode or per-event mode.
type EditorState =
  | { mode: 'defaults'; category?: EventResourceCategory }
  | { mode: 'event'; eventId: number; eventName: string; category: EventResourceCategory }

export function ResourcesDirectory() {
  const navigate = useNavigate()
  const { data, isLoading, error } = useResourcesDirectory()
  const [filter, setFilter] = useState<Filter>('all')
  const [editor, setEditor] = useState<EditorState | null>(null)
  const { hasOverride } = useResourceOverrides()

  const events = data?.events ?? []
  const categories = data?.categories ?? EVENT_RESOURCE_CATEGORIES

  // Per-event coverage = how many categories have count > 0.
  // Computed once per data refetch.
  const eventsWithCoverage = useMemo(
    () =>
      events.map((ev) => {
        const populated = categories.filter((c) => (ev.counts[c] ?? 0) > 0).length
        return { event: ev, populated, total: categories.length }
      }),
    [events, categories]
  )

  const counts = useMemo(() => {
    const populated = eventsWithCoverage.filter((row) => row.populated === row.total).length
    const missing = eventsWithCoverage.filter((row) => row.populated < row.total).length
    return { all: eventsWithCoverage.length, populated, missing }
  }, [eventsWithCoverage])

  const filtered = useMemo(() => {
    switch (filter) {
      case 'populated':
        return eventsWithCoverage.filter((row) => row.populated === row.total)
      case 'missing':
        return eventsWithCoverage.filter((row) => row.populated < row.total)
      default:
        return eventsWithCoverage
    }
  }, [eventsWithCoverage, filter])

  function onCellClick(eventId: number, eventName: string, category: EventResourceCategory) {
    // Per-event-edit mode — opens the editor modal scoped to this
    // (event, category). Pre-existing behaviour of routing to
    // /events/{id}?openResources=... is preserved by holding shift /
    // ctrl / meta — useful as an escape hatch into the live panel.
    setEditor({ mode: 'event', eventId, eventName, category })
  }
  function onEventClick(eventId: number) {
    navigate(`/events/${eventId}`)
  }
  function onDefaultsClick(category?: EventResourceCategory) {
    setEditor({ mode: 'defaults', category })
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl mb-1">Resources directory</h1>
        <p className="text-ink-soft">
          Every event × resource slot in one grid. Spot gaps before they
          surface on event day.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <FilterPill
          label="All"
          count={counts.all}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <FilterPill
          label="Populated"
          count={counts.populated}
          active={filter === 'populated'}
          onClick={() => setFilter('populated')}
        />
        <FilterPill
          label="Missing"
          count={counts.missing}
          active={filter === 'missing'}
          onClick={() => setFilter('missing')}
        />
      </div>

      {isLoading && (
        <div className="text-ink-soft text-sm font-mono">Loading directory…</div>
      )}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load directory</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div className="bg-white border border-line rounded-lg p-8 text-center text-ink-soft">
          <p className="text-sm font-semibold mb-1">No events match this filter.</p>
          <p className="text-xs">
            Try switching to <button
              type="button"
              onClick={() => setFilter('all')}
              className="underline hover:text-ink"
            >All</button> to see every non-archived event.
          </p>
        </div>
      )}

      {editor && (
        <ResourceEditModal
          mode={editor.mode}
          eventId={editor.mode === 'event' ? editor.eventId : undefined}
          eventName={editor.mode === 'event' ? editor.eventName : undefined}
          initialCategory={editor.category}
          onClose={() => setEditor(null)}
        />
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <div className="bg-white border border-line rounded-lg overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-line">
                <th
                  scope="col"
                  className="text-left px-4 py-2 font-semibold text-ink-soft uppercase text-[11px]"
                  style={{ letterSpacing: '0.06em', minWidth: 200 }}
                >
                  Event
                </th>
                {categories.map((c) => (
                  <th
                    key={c}
                    scope="col"
                    className="px-2 py-2 font-semibold text-ink-soft uppercase text-[11px]"
                    style={{ letterSpacing: '0.06em', textAlign: 'center', minWidth: 88 }}
                  >
                    {EVENT_RESOURCE_CATEGORY_LABEL[c]}
                  </th>
                ))}
                <th
                  scope="col"
                  className="px-2 py-2 font-semibold text-ink-soft uppercase text-[11px]"
                  style={{ letterSpacing: '0.06em', textAlign: 'center', minWidth: 88 }}
                >
                  Coverage
                </th>
              </tr>
            </thead>
            <tbody>
              <DefaultsRow
                categories={categories}
                hasOverride={hasOverride}
                onLabelClick={() => onDefaultsClick()}
                onCellClick={(c) => onDefaultsClick(c)}
              />
              {filtered.map(({ event, populated, total }) => (
                <EventRow
                  key={event.eventId}
                  event={event}
                  categories={categories}
                  populated={populated}
                  total={total}
                  onCell={onCellClick}
                  onEvent={onEventClick}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ---- row -------------------------------------------------------------------

function EventRow({
  event,
  categories,
  populated,
  total,
  onCell,
  onEvent
}: {
  event: ResourcesDirectoryEvent
  categories: ReadonlyArray<EventResourceCategory>
  populated: number
  total: number
  onCell: (eventId: number, eventName: string, category: EventResourceCategory) => void
  onEvent: (eventId: number) => void
}) {
  const dateText = formatDateRange(event.startDate, event.endDate)
  const fullyCovered = populated === total

  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-parchment/60 transition-colors">
      <td className="px-4 py-2 align-middle">
        <button
          type="button"
          onClick={() => onEvent(event.eventId)}
          className="text-left hover:text-green transition-colors"
        >
          <div className="font-semibold text-ink">{event.eventName}</div>
          <div className="text-xs text-ink-soft font-mono mt-0.5">{dateText}</div>
        </button>
      </td>
      {categories.map((c) => {
        const count = event.counts[c] ?? 0
        return (
          <td key={c} className="px-2 py-2 align-middle" style={{ textAlign: 'center' }}>
            <CountBadge
              count={count}
              ariaLabel={`Edit ${EVENT_RESOURCE_CATEGORY_LABEL[c]} for ${event.eventName} (${count} items)`}
              onClick={() => onCell(event.eventId, event.eventName, c)}
            />
          </td>
        )
      })}
      <td className="px-2 py-2 align-middle" style={{ textAlign: 'center' }}>
        <CoverageBadge populated={populated} total={total} fullyCovered={fullyCovered} />
      </td>
    </tr>
  )
}

// ---- defaults row --------------------------------------------------------
//
// Sits at the top of the grid. Visually distinct from event rows (brass-
// soft background tint). Clicking the label opens the editor in defaults
// mode with all 6 tabs; clicking any per-category "Edit" badge opens
// scoped to that one tab.
function DefaultsRow({
  categories,
  hasOverride,
  onLabelClick,
  onCellClick
}: {
  categories: ReadonlyArray<EventResourceCategory>
  hasOverride: (scope: 'defaults' | { eventId: number }, category?: 'briefing' | 'plan' | 'first-aid') => boolean
  onLabelClick: () => void
  onCellClick: (category: EventResourceCategory) => void
}) {
  const editable: ReadonlyArray<EventResourceCategory> = ['briefing', 'plan', 'first-aid']
  function isEditable(c: EventResourceCategory): c is 'briefing' | 'plan' | 'first-aid' {
    return (editable as readonly string[]).includes(c)
  }

  return (
    <tr className="border-b border-line bg-brass-soft/40 hover:bg-brass-soft transition-colors">
      <td className="px-4 py-2 align-middle">
        <button
          type="button"
          onClick={onLabelClick}
          className="flex items-center gap-2 text-left hover:text-brass transition-colors"
        >
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-brass-soft border border-brass text-brass">
            <Icon name="edit" size={14} />
          </span>
          <div>
            <div className="font-semibold text-ink">Resources Edit Defaults</div>
            <div className="text-xs text-ink-soft mt-0.5 italic">
              Org-wide preview — stored in your browser.
            </div>
          </div>
        </button>
      </td>
      {categories.map((c) => {
        const edits = isEditable(c)
        const overridden = edits && hasOverride('defaults', c)
        return (
          <td key={c} className="px-2 py-2 align-middle" style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => onCellClick(c)}
              aria-label={
                edits
                  ? `Edit defaults — ${EVENT_RESOURCE_CATEGORY_LABEL[c]}${overridden ? ' (modified)' : ''}`
                  : `${EVENT_RESOURCE_CATEGORY_LABEL[c]} — read-only`
              }
              className={
                'inline-flex items-center gap-1 rounded-md text-xs font-semibold transition-colors ' +
                (edits
                  ? overridden
                    ? 'bg-brass text-white border border-brass hover:brightness-110'
                    : 'bg-white text-brass border border-brass hover:bg-brass hover:text-white'
                  : 'bg-parchment text-ink-soft border border-dashed border-line hover:border-ink-soft')
              }
              style={{ padding: '3px 8px' }}
            >
              <Icon name="edit" size={11} />
              {edits ? (overridden ? 'Tuned' : 'Edit') : 'Read-only'}
            </button>
          </td>
        )
      })}
      <td className="px-2 py-2 align-middle" style={{ textAlign: 'center' }}>
        <span className="text-ink-soft text-xs">—</span>
      </td>
    </tr>
  )
}

function CountBadge({
  count,
  ariaLabel,
  onClick
}: {
  count: number
  ariaLabel: string
  onClick: () => void
}) {
  const empty = count === 0
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={
        'inline-flex items-center justify-center rounded-md text-xs font-semibold font-mono tabular-nums transition-colors ' +
        (empty
          ? 'bg-parchment text-ink-soft border border-dashed border-line hover:border-ink-soft hover:text-ink'
          : 'bg-brass-soft text-brass border border-brass-soft hover:bg-brass hover:text-white')
      }
      style={{ minWidth: 36, padding: '4px 8px' }}
    >
      {empty ? '—' : count}
    </button>
  )
}

function CoverageBadge({
  populated,
  total,
  fullyCovered
}: {
  populated: number
  total: number
  fullyCovered: boolean
}) {
  return (
    <span
      className={
        'inline-flex items-center justify-center rounded-full text-xs font-semibold font-mono tabular-nums ' +
        (fullyCovered
          ? 'bg-green-soft text-green border border-green'
          : 'bg-parchment text-ink-soft border border-line')
      }
      style={{ minWidth: 44, padding: '2px 10px' }}
      aria-label={`${populated} of ${total} categories populated`}
    >
      {populated}/{total}
    </span>
  )
}

// ---- helpers ---------------------------------------------------------------

function formatDateRange(startIso: string, endIso: string): string {
  // Inputs are YYYY-MM-DD calendar dates. Render as e.g. "13 May" or
  // "13 – 15 May" with full ISO precision in the title for hover.
  const start = parseIsoDateOnly(startIso)
  const end = parseIsoDateOnly(endIso)
  if (!start || !end) return `${startIso} – ${endIso}`
  const sameDay = startIso === endIso
  const sameMonth = start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear()
  const startTxt = start.toLocaleDateString('en-GB', sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' })
  const endTxt = end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  return sameDay ? endTxt : `${startTxt} – ${endTxt}`
}

function parseIsoDateOnly(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
  const d = new Date(`${iso}T00:00:00Z`)
  return Number.isNaN(d.getTime()) ? null : d
}
